import Icon from "@components/Atoms/Icon";
import { classNames } from "@components/libs/classNames";
import useWatch from "@core-ui/hooks/useWatch";
import { useIsPrint } from "@ext/print/PrintContext";
import type { IconCode } from "@ui-kit/Icon";
import { type MouseEvent, type ReactElement, useState } from "react";

export enum NoteType {
	quote = "quote",
	lab = "lab",
	tip = "tip",
	info = "info",
	note = "note",
	danger = "danger",
	hotfixes = "hotfixes",
}

export const noteIcons: { [note in NoteType]: IconCode } = {
	quote: "quote",
	lab: "test-tube-diagonal",
	tip: "lightbulb",
	hotfixes: "wrench",
	info: "info",
	note: "triangle-alert",
	danger: "octagon-alert",
};

interface NoteProps {
	type?: NoteType;
	title?: string;
	children?: ReactElement;
	collapsed?: string | boolean;
	className?: string;
	collapseCallback?: (collapse: boolean) => void;
	disableRender?: boolean;
	isPrint?: boolean;
}

const Note = (props: NoteProps): ReactElement => {
	const { type = NoteType.note, title, className, children, collapseCallback, disableRender, isPrint } = props;
	// A collapsed note on paper is an empty frame with a heading: the reader has no way to open it.
	const collapsed = props.collapsed && !isPrint && !useIsPrint();
	const [expanded, dispatchExpanded] = useState(!collapsed);

	const toggleExpanded = (e: MouseEvent<HTMLElement>) => {
		e.preventDefault();
		collapseCallback?.(!expanded);
		dispatchExpanded((p) => !p);
	};

	useWatch(() => {
		dispatchExpanded(!collapsed);
		collapseCallback?.(!collapsed);
	}, [collapsed]);

	const clickable = !expanded || collapsed;

	return (
		<div
			className={classNames("admonition", {}, [
				`admonition-${type}`,
				`admonition-${title ? "column" : "row"}`,
				className,
			])}
			data-component="note"
		>
			<div
				className={classNames("admonition-heading", { expanded: !expanded })}
				onClick={clickable ? toggleExpanded : null}
			>
				<div className="admonition-icon">
					{clickable ? (
						<Icon
							code={expanded ? "chevron-down" : "chevron-right"}
							style={{ cursor: "pointer", userSelect: "none" }}
						/>
					) : (
						<Icon code={noteIcons[type]} strokeWidth="2" />
					)}
				</div>
				<div className="titleWrapper" contentEditable={false} suppressContentEditableWarning={true}>
					<div className={classNames("title", { clickable })}>{title}</div>
				</div>
			</div>
			<div className="admonition-content">
				{(disableRender && !expanded) || (
					<div className={classNames("paragraph", { "content-hide": !expanded })}>{children}</div>
				)}
			</div>
		</div>
	);
};

export const noteFoundElementBeforeHighlightHandler = (foundEl: HTMLElement) => {
	for (
		let admonition = foundEl.closest(".admonition");
		admonition;
		admonition = admonition.parentElement?.closest(".admonition")
	) {
		// The editor marks a collapsed note on the admonition itself, the rendered note on its heading.
		const isCollapsed =
			admonition.classList.contains("admonition-collapsed") ||
			admonition.querySelector(":scope > .admonition-heading")?.classList.contains("expanded");
		if (isCollapsed)
			admonition.querySelector<HTMLElement>(":scope > .admonition-heading .admonition-icon")?.click();
	}
};

export default Note;
